/**
 * @copyright © Paramount Streaming
 * @author Zeeshan Ahmad
 * @description PTV Data Service constants
 */

/**
 * @description Default PTV server URLs.
 * These values are overridden at runtime by the embedding app via `paramsVO.STITCHER.servers`.
 * App Config keys that map to each server are defined in the Streaming URL Configurations doc.
 * @see https://paramount.atlassian.net/wiki/spaces/STC/pages/626429706/Streaming+URL+Configurations+for+Convergence
 */
export const PTV_SERVERS = {
	channels: 'https://service-channels.clusters.pluto.tv',
	vod: 'https://service-vod.clusters.pluto.tv',
	stitcher: 'https://cfd-v4-service-channel-stitcher-use1-1.prd.pluto.tv',
	stitcherDash: 'https://cfd-v4-service-stitcher-dash-use1-1.prd.pluto.tv',
	concierge: 'https://service-concierge.clusters.pluto.tv',
	/** Ad Image Service for pause ads (bootstrap `servers.pause`) */
	pause: 'https://service-ad-image-ga.prd.pluto.tv'
} as const;

/**
 * @description PTV Converged API endpoint paths
 */
export const PTV_API_PATHS_CONVERGED = {
	channels: '/v3.0/guide/channels',
	vodItems: '/v3.5/vod/items',
	vodEpisodes: '/v3.5/vod/episodes',
	// Concierge production endpoints — /v1/wv and /v1/pr require Authorization: Bearer header.
	// FairPlay uses /v1/fp (license, Authorization: Bearer header) and /v1/cert/fp (cert, ?jwt= query param).
	// @see https://paramount.atlassian.net/wiki/spaces/PLTCONTENT/pages/258409590/Service+Concierge
	widevine: '/v1/wv',
	playready: '/v1/pr',
	fairplayLicense: '/v1/fp',
	fairplayCert: '/v1/cert/fp'
} as const;

/**
 * @description PTV legacy API endpoint paths
 */
export const PTV_API_PATHS_LEGACY = {
	channels: '/v2/guide/channels',
	vodItems: '/v4/vod/items',
	vodEpisodes: '/v4/vod/episodes',
	// Concierge production endpoints — /v1/wv and /v1/pr require Authorization: Bearer header.
	// FairPlay uses /v1/fp (license, Authorization: Bearer header) and /v1/cert/fp (cert, ?jwt= query param).
	// @see https://paramount.atlassian.net/wiki/spaces/PLTCONTENT/pages/258409590/Service+Concierge
	widevine: '/v1/wv',
	playready: '/v1/pr',
	fairplayLicense: '/v1/fp',
	fairplayCert: '/v1/cert/fp'
} as const;

/**
 * @description Stitcher configuration constants
 */
export const STITCHER_CONFIG = {
	version: 'v3',
	adTechnology: 'ssai_podserving',
	method: 'polling',
	inactivityTimeout: 5000,
	minimumUpdateThreshold: 1000,
	adBreakPreroll: false,
	adBreakPostroll: false
} as const;

/**
 * @description Content configuration constants
 */
export const CONTENT_CONFIG = {
	partner: 'plutotv',
	autoplay: true,
	adapterId: 'shaka'
} as const;

/**
 * @description Stream type constants
 */
export const STREAM_TYPES = {
	LIVE: 'live',
	VOD: 'vod'
} as const;

/**
 * @description DRM type labels for the Conviva `drmType` tracking field
 * (`resource.metadata.drmType`) on Stitcher (Pluto) playback.
 *
 * Stitcher web serves only three schemes: `Widevine` (DASH), `FairPlay` (Safari HLS), and
 * `None` (non-Apple HLS — AES ClearKey, i.e. no DRM). PlayReady is intentionally absent —
 * Daniel Baird (avia/Stitcher) confirmed 2026-06-04 it was prototyped but never rolled to
 * Stitcher prod. Per PLBCK-6503 the confirmed Conviva values are `Widevine` (case-sensitive)
 * and `None`; `FairPlay` awaits value confirmation pending on PLBCK-6504.
 * @see https://paramount.atlassian.net/browse/PLBCK-6504
 */
export const STITCHER_DRM_TYPE = {
	WIDEVINE: 'Widevine',
	FAIRPLAY: 'FairPlay',
	NONE: 'None'
} as const;

/**
 * @description Player identity constants for resource.metadata tracking fields.
 * `version` is a build-time token replaced by the release version at compile time.
 */
export const PLAYER_INFO = {
	name: 'Smart Tag'
} as const;